import {map} from 'rxjs/operators';
import {AfterViewInit, Component, forwardRef, Input, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import {CompleterCmp, CompleterData, CompleterItem} from 'ng2-completer';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {languages_name, translateName} from '../../functions/language.functions';
import {TranslateService} from '@ngx-translate/core';
import {GbGemeindeSearchMode} from "./gbGemeindeSearchMode";
import {AppRestCompleterService} from "../../../core/services/app-rest-completer.service";
import {GbCompleterGemeindeListEntry} from './gbCompleterGemeindeListEntry';
import {Subscription} from "rxjs";
import {GbGemeindeId, GbGemeindeListEntry, GbgemeindeService} from 'app/core/gb-api';
import {GwrGemeindeId} from 'app/core/gwr-api';
import {AppPermissionService} from '../../../core/services/app-permission.service';
import {CapFormControlComponent} from 'app/shared/form-control/form-control.component';
import {GemeindeLocalData} from "./GemeindeLocalData";

@Component({
  selector: 'cap-gb-gemeinde',
  templateUrl: './gb-gemeinde.component.html',
  styleUrls: ['./gb-gemeinde.component.scss'],
  host: {
    "class": "form-control-component"
  },
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GbGemeindeComponent), multi: true},
    AppRestCompleterService,
  ]
})

export class GbGemeindeComponent implements OnInit, AfterViewInit, ControlValueAccessor, OnDestroy {

  @Input()
  searchMode: GbGemeindeSearchMode;

  @Input()
  nurGueltige = true;

  @Input()
  showKreisnummer = true;

  @Input()
  hasfocus = false;

  @Input()
  mitExternen = true;

  @Input()
  mitAufSuchmaskeVerborgenen = false;


  @ViewChild(CompleterCmp, {static: true})
  completer: CompleterCmp;

  public isOpen = false;
  openCloseFocused;
  dataService: CompleterData;
  value = '';

  propagateChange: (id: string) => void;
  propagateTouch: (_) => void;

  private reset = false;
  private gemeindeSubscription: Subscription;
  hasPermissionSeybuch: boolean;
  private setValueFromOutside: boolean;

  constructor(private completerService: AppRestCompleterService,
              private translate: TranslateService,
              private gbGemeindeService: GbgemeindeService,
              private permissionService: AppPermissionService,
              @Optional() private formControlComponent: CapFormControlComponent) {
  }

  ngAfterViewInit() {
    if (this.formControlComponent) {
      this.formControlComponent.registerChild(this.completer.ctrInput);
    }
    this.completer.completer.open();
  }


  ngOnInit() {
    this.checkPermissionSeybuch();
    const findAll = this.gbGemeindeService.loadGbGemeindeListEntries(
      GbGemeindeSearchMode[this.searchMode], //
      this.nurGueltige,
      this.mitExternen,
      this.hasPermissionSeybuch,
      this.mitAufSuchmaskeVerborgenen)//
      .pipe(
        map(data => this.changeObject(data)),
      );
    const currentLanguageProperty = languages_name[this.translate.currentLang].name;
    const searchFields = currentLanguageProperty + ',bfsNummer,kreisNummer,kuerzel,title';
    this.dataService = this.completerService.gemeindeLocal(findAll, searchFields, 'title');
  }


  onBlur() {
    this.completer.completer.selectCurrent();
    if (!this.completer.completer.hasSelected && !this.setValueFromOutside) {
      this.value = '';
      this.propagateChange(null);
    }
    this.setValueFromOutside = false;
  }

  public focus() {
    this.completer.focus();
    this.hasfocus = true;
  }

  writeValue(id: GbGemeindeId | GwrGemeindeId): void {
    if (id) {
      this.gemeindeSubscription = this.gbGemeindeService.loadGbGemeindeListEntryById(id.id).subscribe(gemeinde => {
        this.value = translateName(gemeinde.name, this.translate) + ' ' + gemeinde.bfsNummer;
        const selectedItem = {title: this.value, originalObject: gemeinde};
        this.completer.completer.onSelected(selectedItem);
      });
      this.reset = false;
    } else if (!this.reset) {
      this.reset = true;
      this.value = '';
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public onOpened(isOpen: boolean) {
    this.isOpen = isOpen;
  }

  onClick() {
    if (this.completer.disableInput === true) {
      this.completer.close();
    } else {
      if (this.isOpen) {
        this.value = '';
        this.propagateChange(null);
      }
    }
  }

  onChange() {
    this.gemeindeSubscription?.unsubscribe();
  }

  onSelected(selectedItem: CompleterItem) {
    if (selectedItem) {
      this.value = selectedItem.title;
      this.propagateChange(selectedItem.originalObject.id);
      this.reset = false;
    }
  }

  public emptyCompleter() {
    this.completer.value = "";
    this.value = "";
    this.propagateChange(null);
    this.onSelected(null);
  }

  public setCompleterValue(id: GbGemeindeId | GwrGemeindeId) {
    this.writeValue(id);
    this.setValueFromOutside = true;
  }

  private changeObject(gemeindeList: Array<GbGemeindeListEntry>) {
    const tempArray: Array<GbCompleterGemeindeListEntry> = new Array<GbCompleterGemeindeListEntry>();
    for (const entry of gemeindeList) {
      tempArray.push(this.changeDatatype(entry));
    }
    return tempArray;
  }

  private changeDatatype(entry: GbGemeindeListEntry): GbCompleterGemeindeListEntry {
    const completerEntry: GbCompleterGemeindeListEntry = <GbCompleterGemeindeListEntry>{...entry};
    const currentLanguageProperty = languages_name[this.translate.currentLang].name;
    let title = completerEntry[currentLanguageProperty];
    if (completerEntry.bfsNummer) {
      title += ' ' + completerEntry.bfsNummer;
    }
    if (this.showKreisnummer && completerEntry.kreisNummer) {
      title += '.' + completerEntry.kreisNummer;
    }
    completerEntry.title = title;
    return completerEntry;
  }

  checkPermissionSeybuch() {
    this.hasPermissionSeybuch = this.permissionService.hasPermission("SEYBUCH_REGISTER");
  }

  ngOnDestroy(): void {
    this.gemeindeSubscription?.unsubscribe();
  }

}
